import React, { useState } from 'react';
import CurrencyFormat from 'react-currency-format';
import BetTable from './BetTable';
import TradeDetailModal from './TradeDetailModal';
import WegaBetDetail from './WegaBetDetail';
import '../styles/styles.css';

const Money = ({ value, className }) => (
  <CurrencyFormat
    value={value}
    decimalScale={2}
    displayType={'text'}
    thousandSeparator={true}
    fixedDecimalScale={true}
    className={className}
  />
);

/**
 * The games (Wega/SABA) rows behind a Net W/L number, in the sports listing's columns.
 *
 * <p>Same columns, same order and same cell meanings as {@code BetList} - except Score, which a games row
 * has nothing to fill: No · Username · Date · Reference · Bet Type · Bet Details · Amount · Turnover ·
 * Comm · Net Amt · Result · Winning. A games row carries no teams, no odds, no handicap and no score, so
 * this is still its own component over its own reader rather than a filter over the sports list - but a
 * reader moving between the two segments of one screen reads one layout (Andrew, 2026-09-22).</p>
 *
 * <p>The money columns are always shown: this list is only ever rendered on the agent portal for an agent
 * caller, which is the case the sports list's {@code agent === 'Y'} gate exists for. The footer is one row
 * on the sports shape: {@code Net Amt} is turnover less commission and {@code Winning} is winning plus
 * commission, so neither figure is a second definition of the other column beside it.</p>
 *
 * <p>The list owns the trade-detail modal, not its screens: every games row on every screen that lists one
 * opens from here, so the modal is mounted once with the list that shows the rows and the screens pass only
 * where their family reads a ticket ({@code tradeDetailUrl}) - the reports' endpoint or the balance report's,
 * which differ because a sub-user's privileges are per screen family. A screen that passes nothing gets a
 * list whose references are plain text: a visible gap, rather than a link that asks the wrong endpoint.</p>
 */
const WegaBetList = ({
  bets,
  handleChange,
  totalPage,
  amountTotal,
  turnoverTotal,
  commissionTotal,
  winningTotal,
  tradeDetailUrl,
}) => {
  const rows = bets || [];
  // the row whose ticket is up, by public id: the modal reads one bet, so nothing else is held here
  const [pubId, setPubId] = useState(null);

  return (
    <>
      <BetTable
        columns={[
          { label: 'No' },
          { label: 'Username' },
          { label: 'Date' },
          { label: 'Reference' },
          { label: 'Bet Type' },
          { label: 'Bet Details' },
          { label: 'Amount' },
          { label: 'Turnover' },
          { label: 'Comm' },
          { label: 'Net Amt' },
          { label: 'Result' },
          { label: 'Winning' },
        ]}
        body={
          <tbody>
            {rows.map((bet, index) => (
              <WegaBetDetail bet={bet} index={index} key={index} onReference={setPubId} />
            ))}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={12} className="text-center">
                  No bet history
                </td>
              </tr>
            ) : null}
          </tbody>
        }
        footer={
          rows.length > 0 ? (
            <tfoot className="font-weight-bold">
              <tr>
                <td className="text-center" colSpan={6}>
                  Total
                </td>
                <td className="text-right">
                  <Money value={amountTotal} />
                </td>
                <td className="text-right">
                  <Money value={turnoverTotal} />
                </td>
                <td className="text-right">
                  <Money value={commissionTotal} />
                </td>
                <td
                  className={`text-right ${
                    turnoverTotal - commissionTotal < 0 ? 'text-danger' : ''
                  }`}
                >
                  <Money value={turnoverTotal - commissionTotal} />
                </td>
                <td>&nbsp;</td>
                <td className={`text-right ${winningTotal < 0 ? 'text-danger' : ''}`}>
                  <Money value={winningTotal} />
                </td>
              </tr>
            </tfoot>
          ) : null
        }
        totalPage={totalPage}
        handleChange={handleChange}
      />
      {/* mounted with the list, not with a screen: whichever segment is open, the row that was opened and the
          ticket it mints live here, and closing drops both */}
      <TradeDetailModal endpoint={tradeDetailUrl} pubId={pubId} onClose={() => setPubId(null)} />
    </>
  );
};

export default WegaBetList;
